/**
 * 現在ページのスナップショット (BINDEC_SHIPPING_SETTINGS.addressCustomData / window.customerCustomData)
 * を取得する。Section API 失敗時のフォールバック、および取得結果との比較に使用。
 */
function getSnapshotAddressCustomData() {
  const snap =
    (window.BINDEC_SHIPPING_SETTINGS && window.BINDEC_SHIPPING_SETTINGS.addressCustomData) ||
    (typeof customerCustomData !== 'undefined' ? customerCustomData : null) ||
    (window.customerCustomData) ||
    null;
  if (!snap || typeof snap !== 'object') return { addresses: [] };
  return { addresses: Array.isArray(snap.addresses) ? snap.addresses : [], ...snap };
}

/**
 * サーバから最新の customer.metafields.itfor.address_custom_data を取得する。
 * Section API でキャッシュバスター付きで取得し、ページロード時のスナップショット
 * (window.customerCustomData / BINDEC_SHIPPING_SETTINGS.addressCustomData) ではなく
 * 書き込み直前の最新値を read-modify-write の起点に使うためのヘルパー。
 *
 * 注意: Section API はレンダリングパスやキャッシュの都合で稀に空オブジェクトを返すことがある。
 * 取得結果の addresses 件数がスナップショットより少ない場合、データ破壊を防ぐため
 * スナップショット側にフォールバックする（保守的な動作）。
 *
 * @returns {Promise<{addresses: Array} | null>} 取得失敗時は null（呼び出し側で書き込み中止判断）
 */
async function fetchLatestAddressCustomData() {
  const snapshot = getSnapshotAddressCustomData();
  try {
    const url = `/account/addresses?section=wl-new-added-address&_=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) {
      console.warn('[fetchLatestAddressCustomData] section fetch failed, falling back to snapshot:', res.status);
      return snapshot;
    }
    const html = new DOMParser().parseFromString(await res.text(), 'text/html');
    const jsonText = html.querySelector('#wlAddressCustomDataJson')?.textContent?.trim();
    if (!jsonText || jsonText === 'null') {
      // メタフィールド未登録 or Section API がレンダリング時に customer.metafields を取れなかった
      // → スナップショットにフォールバック（破壊を防ぐ）
      console.warn('[fetchLatestAddressCustomData] empty/null JSON in section response, falling back to snapshot');
      return snapshot;
    }
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      console.warn('[fetchLatestAddressCustomData] JSON parse failed, falling back to snapshot:', e);
      return snapshot;
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.addresses)) {
      console.warn('[fetchLatestAddressCustomData] unexpected structure, falling back to snapshot');
      return snapshot;
    }
    // 取得結果がスナップショットより addresses 件数が少ない場合、Section API の問題を疑い
    // スナップショットを採用する（既存データ破壊の防止）
    if (parsed.addresses.length < snapshot.addresses.length) {
      console.warn(
        '[fetchLatestAddressCustomData] fetched addresses fewer than snapshot, falling back to snapshot',
        { fetched: parsed.addresses.length, snapshot: snapshot.addresses.length }
      );
      return snapshot;
    }
    return parsed;
  } catch (e) {
    console.warn('[fetchLatestAddressCustomData] error, falling back to snapshot:', e);
    return snapshot;
  }
}

/**
 * `CustomerFields` クラスは、カスタマーフォームのバリデーションとフィールドの制御を行うためのカスタム要素です。
 *
 * 主な機能:
 * - フォームフィールドのバリデーション
 * - パスワードの一致確認
 * - 必須フィールドのチェック
 * - フォームフィールドの表示/非表示の制御
 *
 * メソッド:
 * - `validatePasswordMatch(event)`: パスワードと確認用パスワードが一致するかを確認します。
 * - `showAlert(el, message)`: 指定された要素にエラーメッセージを表示します。
 * - `hideAlert(el, message)`: 指定された要素からエラーメッセージを非表示にします。
 * - `validateRequired(event)`: 必須フィールドのバリデーションを行います。
 * - `validateInt(event)`: 整数フィールドのバリデーションを行います。
 * - `validateInt7(event)`:郵便番号フィールドのバリデーションを行います。
 * - `validateFullWidth(event)`: 全角文字フィールドのバリデーションを行います。
 * - `validateHalfWidth(event)`: 半角文字フィールドのバリデーションを行います。
 * - `validateFullWidthKana(event)`: 全角カナフィールドのバリデーションを行います。
 * - `validateEmail(event)`: メールアドレスフィールドのバリデーションを行います。
 * - `validateConformEmail(event)`: 確認用メールアドレスフィールドのバリデーションを行います。
 * - `validatePassword(event)`: パスワードフィールドのバリデーションを行います。
 * - `checkValidity()`: フォーム全体のバリデーションを行います。
 */
class CustomerFields extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submit_button = this.querySelector('button#cf-submit');
    this.inputs = this.querySelectorAll('input');
    this.full_width_input = this.querySelectorAll('input[data-cf-validation="full-width"]');
    this.full_width_kana_input = this.querySelectorAll('input[data-cf-validation="full-width-kana"]');
    this.harf_width_input = this.querySelectorAll('input[data-cf-validation="half-width"]');
    this.int_input = this.querySelectorAll('input[data-cf-validation="int"]');
    this.int_7_input = this.querySelectorAll('input[data-cf-validation="int-7"]');
    this.letters_numbers_no_symbols_input = this.querySelectorAll('input[data-cf-validation="letters-numbers-no-symbols"]');
    this.required_input = this.querySelectorAll('input[data-cf-required="true"], select[data-cf-required="true"]');
    this.mail_input = this.querySelector('input[name="email"]');
    this.mail_confirm_input = this.querySelector('input[name="email_confirmation"]');
    this.password_input = this.querySelector('input[name="password"]');
    this.password_confirm_input = this.querySelector('input[name="password_confirm"]');

    this.ERROR_MESSAGES = {
      REQUIRED: '※ このフィールドは必須です。',
      FULL_WIDTH: '※ 必ず全角文字を入力してください(例：３００ -１　この場合、“３００“の直後に半角が紛れています)。',
      FULL_WIDTH_KANA: '※ 全角カナを入力してください。',
      HALF_WIDTH: '※ 半角文字を入力してください。',
      EMAIL: '※ 有効なメールアドレスを入力してください。',
      EMAIL_CONFIRM: '※ メールアドレスが一致しません。',
      PASSWORD: '※ 半角英数字の組み合わせで「5文字」以上「20文字」以下で入力してください。',
      PASSWORD_CONFIRM: '※ パスワードが一致しません。',
      INT: '※ 半角数字を入力してください。',
      INT_7: '※ 7桁の半角数字を入力してください。',
      BIRTHDAY: '※ 生年月日を入力してください。',
      NO_SYMBOLS: '※ 記号は入力できません。',
    };

    this.emailValue = '';
    this.passwordValue = '';

    this.setupValidators();
  }

  setupValidators() {
    this.full_width_input?.forEach((input) => {
      input.addEventListener('change', this.validateFullWidth.bind(this));
    });

    this.full_width_kana_input?.forEach((input) => {
      input.addEventListener('change', this.validateFullWidthKana.bind(this));
    });

    this.harf_width_input?.forEach((input) => {
      input.addEventListener('change', this.validateHalfWidth.bind(this));
    });

    this.int_input?.forEach((input) => {
      input.addEventListener('change', this.validateInt.bind(this));
    });

    this.int_7_input?.forEach((input) => {
      input.addEventListener('change', this.validateInt7.bind(this));
    });

    this.required_input?.forEach((input) => {
      input.addEventListener('change', this.validateRequired.bind(this));
    });

    this.letters_numbers_no_symbols_input?.forEach((input) => {
      input.addEventListener('change', this.validateLettersNumbersNoSymbols.bind(this));
    });

    this.mail_input?.addEventListener('change', this.validateEmail.bind(this));
    this.mail_confirm_input?.addEventListener('change', this.validateConformEmail.bind(this));
    this.password_input?.addEventListener('change', this.validatePassword.bind(this));
    this.password_confirm_input?.addEventListener('change', this.validatePasswordMatch.bind(this));


    this.__checkValidityListener = this.checkValidity.bind(this);
    this.submit_button?.addEventListener('click', this.__checkValidityListener);
  }

  checkValidity() {
    this.required_input.forEach((input) => {
      this.validateRequired({ target: input });
    });
    this.full_width_input?.forEach((input) => {
      this.validateFullWidth({ target: input });
    });
    this.full_width_kana_input?.forEach((input) => {
      this.validateFullWidthKana({ target: input });
    });
    this.harf_width_input?.forEach((input) => {
      this.validateHalfWidth({ target: input });
    });
    this.int_input?.forEach((input) => {
      this.validateInt({ target: input });
    });
    this.int_7_input?.forEach((input) => {
      this.validateInt7({ target: input });
    });
    this.letters_numbers_no_symbols_input?.forEach((input) => {
      this.validateLettersNumbersNoSymbols({ target: input });
    });
    const hasError = this.querySelector('.cf-error[style*="display: block"]');
    if (this.form.checkValidity() && !hasError) {
      // Disable submit button to prevent double submission
      if (this.submit_button) {
        this.submit_button.disabled = true;
        this.submit_button.setAttribute('aria-busy', 'true');
      }
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      this.form.dispatchEvent(submitEvent);
    } else {
      // Re-enable submit button if there are validation errors
      if (this.submit_button) {
        this.submit_button.disabled = false;
        this.submit_button.removeAttribute('aria-busy');
      }

      // scroll to the first error
      const firstError = this.querySelector('dd:not([disabled]) .cf-error[style*="display: block"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  showAlert(el, message) {
    const name = el.getAttribute('name');
    const errorElement = this.querySelector(`.cf-error[data-cf-field="${name}"]`);

    if (!errorElement) {
      console.warn(`Error element not found for input:`, el);
      return;
    }

    let shouldAddMessage = true;
    const errorMessages = errorElement.querySelectorAll('.cf-error-message');
    
    // Check if the error message already exists
    if (errorMessages && errorMessages.length > 0) {
      const messagesList = Array.from(errorMessages);
      shouldAddMessage = !messagesList.some(item => item.textContent === message);
    }

    if (shouldAddMessage) {
      errorElement.innerHTML += `<div class="cf-error-message">${message}</div>`;
      errorElement.style.display = 'block';
    }
  }

  hideAlert(el, message) {
    const name = el.getAttribute('name');
    const errorElement = this.querySelector(`.cf-error[data-cf-field="${name}"]`);
    
    if (!errorElement) return;

    const errorMessages = errorElement.querySelectorAll('.cf-error-message');
    const errorMessagesApp = errorElement.querySelectorAll('p');

    errorMessages.forEach((errorMessage) => {
      if (errorMessage.textContent === message) {
        errorMessage.remove();
      }
    });
    errorMessagesApp.forEach((errorMessage) => {
      errorMessage.remove();
    });
    if (errorElement.children.length === 0) {
      errorElement.style.display = 'none';
    }
  }

  validateRequired(event) {
    const input = event.target;
    const value = input.type === 'checkbox' ? input.checked : input.value;

    if (input.disabled) {
      return;
    }

    if (value === '' || value === false) {
      this.showAlert(input, this.ERROR_MESSAGES.REQUIRED);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED);
    }
  }

  validateInt(event) {
    const input = event.target;
    const name = input.value;

    if (name === '' || /^[0-9]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.INT);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.INT);
    }
  }

  validateInt7(event) {
    const input = event.target;
    const value = input.value;

    if (value === '' || /^[0-9]{7}$/.test(value)) {
      this.hideAlert(input, this.ERROR_MESSAGES.INT_7);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.INT_7);
    }
  }

  validateHalfWidth(event) {
    const input = event.target;
    const name = input.value;

    if (name === '' || /^[\x01-\x7E\uFF61-\uFF9F]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.HALF_WIDTH);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.HALF_WIDTH);
    }
  }

  validateFullWidth(event) {
    const input = event.target;
    const name = input.value;
    const fullWidthPattern =
      /^[\u3000-\u303F\u3040-\u309F\u30A0-\u30FF\uFF01-\uFF60\uFFA0-\uFFEF\u4E00-\u9FAF\u3400-\u4DBF\uF900-\uFAFF]+$/;

    if (name === '' || fullWidthPattern.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH);
    }
  }

  validateFullWidthKana(event) {
    const input = event.target;
    const name = input.value;

    if (name === '' || /^[ァ-ンヴー]*$/.test(name)) {
      this.hideAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.FULL_WIDTH_KANA);
    }
  }

  validateLettersNumbersNoSymbols(event) {
    const input = event.target;
    const value = input.value;
    // 許可: 数字(半角/全角)、英字(半角/全角)、ひらがな、カタカナ(半角/全角)、漢字、スペース(半角/全角)
    // 禁止: 記号類
    const pattern = /^[0-9\uFF10-\uFF19A-Za-z\uFF21-\uFF3A\uFF41-\uFF5A\u3040-\u309F\u30A0-\u30FF\uFF61-\uFF9F\u3400-\u4DBF\u4E00-\u9FFF\u3000 ]+$/u;

    if (value === '' || pattern.test(value)) {
      this.hideAlert(input, this.ERROR_MESSAGES.NO_SYMBOLS);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.NO_SYMBOLS);
    }
  }

  validateEmail(event) {
    const input = event.target;
    const email = input.value;
    this.emailValue = email;

    if (email === '' || /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(email)) {
      this.hideAlert(input, this.ERROR_MESSAGES.EMAIL);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.EMAIL);
    }
    // this.validateConformEmail({ target: this.querySelector('input[name="email_confirmation"]') });
  }

  validateConformEmail(event) {
    const input = event.target;
    const confirm_mail = input.value;
    const email = this.emailValue;

    if (email !== confirm_mail) {
      this.showAlert(input, this.ERROR_MESSAGES.EMAIL_CONFIRM);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.EMAIL_CONFIRM);
    }
  }

  validatePassword(event) {
    const input = event.target;
    const password = input.value;
    this.passwordValue = password;

    if (password !== '' && /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]{5,20}$/.test(password)) {
      this.hideAlert(input, this.ERROR_MESSAGES.PASSWORD);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.PASSWORD);
    }
    //this.validatePasswordMatch({ target: this.querySelector('input[name="password_confirm"]') });
  }

  validatePasswordMatch(event) {
    const input = event.target;
    const confirm_password = input.value;
    const password = this.passwordValue;

    if (password !== confirm_password) {
      this.showAlert(input, this.ERROR_MESSAGES.PASSWORD_CONFIRM);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.PASSWORD_CONFIRM);
    }
  }
}

/**
 * `CustomerFieldsSwitcher` クラスは、`CustomerFields` クラスを拡張し、
 * フォームの値に基づいてフィールドの表示/非表示を切り替える機能を追加します。
 *
 * 主な機能:
 * - フォームの値に基づいてフィールドの表示/非表示を切り替える
 * - 初期値に基づいてフィールドの状態を設定する
 *
 * メソッド:
 * - `controlFormFieldsByValue(event)`: フォームの値に基づいてフィールドの表示/非表示を制御します。
 */
class CustomerFieldsSwitcher extends CustomerFields {
  constructor() {
    super();
    this.member_ship_type = this.querySelectorAll('input[name="mf.ba_customer_fields.type"]');
    this.checked_member_ship_types = Array.from(this.member_ship_type).filter((input) => input.checked);
    this.industry_select = this.querySelector('select[name="mf.ba_customer_fields.industry"]');
    this.accepts_marketing_input = this.querySelectorAll('input[type="radio"][name="acceptsMarketing1"]');
    this.birthday_input = this.querySelector('input[data-cf-validation="birthday"]#cf-birthday');
    this.__checkValidityListener = this.checkValidity.bind(this);

    this.member_ship_type.forEach((input) => {
      input.addEventListener('change', this.controlFormFieldsByValue.bind(this));
    });

    this.accepts_marketing_input?.forEach((input) => {
      input.addEventListener('change', this.validateAccepssMarketing.bind(this));
    });

    // 初期値を設定
    const initialInput = this.checked_member_ship_types[0] || this.member_ship_type[0]; // 初期値として最初の要素を選択
    if (initialInput) {
      initialInput.checked = true; // 必要に応じてチェックを設定
      const event = new Event('change');
      initialInput.dispatchEvent(event); // change イベントをディスパッチ
    }
  }

  validateRequired(event) {
    const input = event.target;
    const value = input.type === 'checkbox' ? input.checked : input.value;

    const parentElement = input.closest('dd'); // 親要素を取得
    
    if (!parentElement) return;

    const parentStyle = window.getComputedStyle(parentElement);

    if (input.disabled || parentStyle.display === 'none') {
      return;
    }

    if (value === '' || value === false) {
      this.showAlert(input, this.ERROR_MESSAGES.REQUIRED);
    } else {
      this.hideAlert(input, this.ERROR_MESSAGES.REQUIRED);
    }
  }

  async checkValidity() {
    this.required_input.forEach((input) => {
      this.validateRequired({ target: input });
    });
    this.full_width_input?.forEach((input) => {
      this.validateFullWidth({ target: input });
    });
    this.full_width_kana_input?.forEach((input) => {
      this.validateFullWidthKana({ target: input });
    });
    this.harf_width_input?.forEach((input) => {
      this.validateHalfWidth({ target: input });
    });
    this.int_input?.forEach((input) => {
      this.validateInt({ target: input });
    });
    this.int_7_input?.forEach((input) => {
      this.validateInt7({ target: input });
    });
    this.letters_numbers_no_symbols_input?.forEach((input) => {
      this.validateLettersNumbersNoSymbols({ target: input });
    });

    // validate birthday
    this.validateBirthDay({ target: this.birthday_input });

    const hasError = this.querySelector('dd:not([disabled]) .cf-error[style*="display: block"]');
    if (!hasError) {
      // Disable submit button to prevent double submission
      if (this.submit_button) {
        this.submit_button.disabled = true;
        this.submit_button.setAttribute('aria-busy', 'true');
      }
      const formData = new FormData(this.form);

      // 顧客メタフィールドに電話番号を設定
      const phone_1_mf = this.form.querySelector('input[name="mf.itfor.rits_phone_1"]');
      const phone_2_mf = this.form.querySelector('input[name="mf.itfor.rits_phone_2"]');
      const phone_3_mf = this.form.querySelector('input[name="mf.itfor.rits_phone_3"]');

      if (phone_1_mf) phone_1_mf.value = formData.get('phone_1') || '';
      if (phone_2_mf) phone_2_mf.value = formData.get('phone_2') || '';
      if (phone_3_mf) phone_3_mf.value = formData.get('phone_3') || '';

      // Set customer address metafields
      const addressMetafields = {
        prefecture: this.form.querySelector('input[name="mf.itfor.rits_address_prefecture"]'),
        city: this.form.querySelector('input[name="mf.itfor.rits_address_city"]'),
        town: this.form.querySelector('input[name="mf.itfor.rits_address_town"]'),
        line1: this.form.querySelector('input[name="mf.itfor.rits_address_line1"]'),
        line2: this.form.querySelector('input[name="mf.itfor.rits_address_line2"]'),
      };

      if (addressMetafields.prefecture) {
        addressMetafields.prefecture.value = formData.get('provinceCode') || '';
      }
      
      // Get town and address1 values from form
      const town = formData.get('town') || formData.get('address[town]') || '';
      const address1 = formData.get('address1') || formData.get('address[address1]') || '';
      
      // Handle address overflow logic: city -> town -> address1 -> address2
      const processedAddress = this.processAddressData && this.processAddressData(
        formData.get('city') || formData.get('address[city]') || '',
        town,
        address1,
        formData.get('address2') || formData.get('address[address2]') || ''
      );
      
      // 町域と番地を結合して Shopify address1 に送る（要件: 番地と合わせて address1 に登録）
      let finalAddress1;
      if (processedAddress) {
        const townPart = processedAddress.town || '';
        const line1Part = processedAddress.address1 || '';
        finalAddress1 = [townPart, line1Part].filter(Boolean).join(' ').trim() || line1Part || townPart;
      } else {
        finalAddress1 = [town, address1].filter(Boolean).join(' ').trim() || address1 || '';
      }
      
      // dispatch の前に address1 の値を town + address1 の結合値に書き換える
      // ba-customerfields が name="address1" を直接読み取って Shopify の住所に保存するため、
      // address1 フィールド自体を結合値にしておく必要がある
      const profileAddress1Input = this.form.querySelector('input[name="address1"]');
      let originalAddress1Value = '';
      if (profileAddress1Input) {
        originalAddress1Value = profileAddress1Input.value;
        profileAddress1Input.value = finalAddress1;

        let shopifyAddress1Hidden = this.form.querySelector('input[name="address[address1]"]');
        if (!shopifyAddress1Hidden) {
          shopifyAddress1Hidden = document.createElement('input');
          shopifyAddress1Hidden.type = 'hidden';
          shopifyAddress1Hidden.name = 'address[address1]';
          this.form.appendChild(shopifyAddress1Hidden);
        }
        shopifyAddress1Hidden.value = finalAddress1;
      }

      // Set metafields separately
      if (processedAddress) {
        if (addressMetafields.city) addressMetafields.city.value = processedAddress.city;
        if (addressMetafields.town) addressMetafields.town.value = processedAddress.town;
        if (addressMetafields.line1) addressMetafields.line1.value = processedAddress.address1;
        if (addressMetafields.line2) addressMetafields.line2.value = processedAddress.address2;
      } else {
        // Fallback: use original values
        if (addressMetafields.city) addressMetafields.city.value = formData.get('city') || formData.get('address[city]') || '';
        if (addressMetafields.town) addressMetafields.town.value = town;
        if (addressMetafields.line1) addressMetafields.line1.value = address1;
        if (addressMetafields.line2) addressMetafields.line2.value = formData.get('address2') || formData.get('address[address2]') || '';
      }

      // Handle custom fields for custom data
      const isCustomDataValid = this.controlCustomFields();

      if (!isCustomDataValid) {
        const shopifyCustomer = window.shopifyCustomer || {};
        const townForCustomData = processedAddress ? (processedAddress.town || '') : town;
        const addressLine1ForCustomData = processedAddress ? (processedAddress.address1 || '') : address1;

        // アドレス敬称_デフォルト値
        const defaultHonorific = '様';

        shopifyCustomer.customData = {
          ...shopifyCustomer.customData,
          sex: formData.get('gender'),
          birthday: formData.get('birthday'),
          last_name_kana: formData.get('lastNameKana'),
          first_name_kana: formData.get('firstNameKana'),
          job_title: formData.get('jobTitle'),
          honorific: defaultHonorific,
          sender: formData.get('sender') === '1',
          phone_1: formData.get('phone_1') || '',
          phone_2: formData.get('phone_2') || '',
          phone_3: formData.get('phone_3') || '',
          town: townForCustomData,
          address_line1: addressLine1ForCustomData,
        };
        if (shopifyCustomer?.customData?.gender) {
          delete shopifyCustomer.customData.gender;
        }
        // ★ 書き戻し先の住所IDをここで確定させて一緒に退避する。
        //   以前は会員トップ側で「住所一覧の最後の要素」を新規住所とみなしていたため、
        //   アドレス帳に配送先があると本人(依頼主)のカナ・町域・番地が配送先へ書き込まれていた。
        //   customer_address_id / default_address_id は会員編集フォームから渡ってくる。
        //   会員登録時はまだ住所が無いので null。会員トップ側で default_address.id を使う。
        shopifyCustomer.targetAddressId =
          this.dataset?.customerAddressId || this.dataset?.defaultAddressId || null;
        shopifyCustomer.savedAt = new Date().toISOString();
        localStorage.setItem('shopifyCustomer', JSON.stringify(shopifyCustomer));
      }

      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      this.form.dispatchEvent(submitEvent);
      if (profileAddress1Input) {
        profileAddress1Input.value = originalAddress1Value;
      }

      // ba-customerfields はデフォルト住所を更新するが、会員プロフィール用住所（customer_address_id）が
      // デフォルト住所と異なる場合、profile_address の氏名・住所も更新する必要がある
      const profileAddressId = this.dataset?.customerAddressId;
      if (profileAddressId) {
        setTimeout(async () => {
          try {
            const fd = new FormData(this.form);
            const addressFormData = new FormData();
            addressFormData.append('address[first_name]', fd.get('firstName') || '');
            addressFormData.append('address[last_name]', fd.get('lastName') || '');
            addressFormData.append('address[zip]', fd.get('zip') || '');
            addressFormData.append('address[province]', fd.get('provinceCode') || '');
            addressFormData.append('address[city]', fd.get('city') || '');
            const townVal = fd.get('town') || '';
            const addr1Val = fd.get('address1') || '';
            const processedAddr = this.processAddressData && this.processAddressData(
              fd.get('city') || '', townVal, addr1Val, fd.get('address2') || ''
            );
            if (processedAddr) {
              addressFormData.set('address[city]', processedAddr.city);
              const finalAddr1 = [processedAddr.town, processedAddr.address1].filter(Boolean).join(' ').trim();
              addressFormData.append('address[address1]', finalAddr1 || '');
              addressFormData.append('address[address2]', processedAddr.address2 || '');
            } else {
              const finalAddr1 = [townVal, addr1Val].filter(Boolean).join(' ').trim();
              addressFormData.append('address[address1]', finalAddr1 || '');
              addressFormData.append('address[address2]', fd.get('address2') || '');
            }
            const p1 = fd.get('phone_1') || '';
            const p2 = fd.get('phone_2') || '';
            const p3 = fd.get('phone_3') || '';
            if (p1 && p2 && p3) {
              addressFormData.append('address[phone]', `${p1}-${p2}-${p3}`);
            }
            addressFormData.append('address[company]', fd.get('company') || '');
            addressFormData.append('address[country]', 'JP');
            addressFormData.append('_method', 'put');

            const response = await fetch(`/account/addresses/${profileAddressId}`, {
              method: 'POST',
              body: addressFormData,
              redirect: 'manual',
            });
            console.log('[CustomerFields] Profile address updated:', profileAddressId, response.type, response.status);
          } catch (err) {
            console.error('[CustomerFields] Failed to update profile address:', err);
          }
        }, 500);
      }
    } else {
      // Re-enable submit button if there are validation errors
      if (this.submit_button) {
        this.submit_button.disabled = false;
        this.submit_button.removeAttribute('aria-busy');
      }

      // scroll to the first error
      const firstError = this.querySelector('dd:not([disabled]) .cf-error[style*="display: block"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  validateAccepssMarketing(event) {
    const input = event.target;
    this.form.querySelector('input[name="acceptsMarketing"]').checked = input.value === 'true';
  }

  validateBirthDay(event) {
    const input = event.target;
    const value = input.value;
    const regex = /^[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}$/;

    if (value === '' || regex.test(value)) {
      this.hideAlert(input, this.ERROR_MESSAGES.BIRTHDAY);
    } else {
      this.showAlert(input, this.ERROR_MESSAGES.BIRTHDAY);
    }
  }

  // Helper method to process address data with cascading overflow logic
  // This is called during form submission to ensure proper data format
  processAddressData(city, town, address1, address2) {
    let processedCity = city || '';
    let processedTown = town || '';
    let processedAddress1 = address1 || '';
    let processedAddress2 = address2 || '';
    
    // Step 1: If city exceeds 10 characters, move overflow to town
    if (processedCity.length > 10) {
      const cityOverflow = processedCity.substring(10);
      processedCity = processedCity.substring(0, 10);
      processedTown = processedTown + cityOverflow;
    }
    
    // Step 2: If town exceeds 10 characters, move overflow to address1
    if (processedTown.length > 10) {
      const townOverflow = processedTown.substring(10);
      processedTown = processedTown.substring(0, 10);
      processedAddress1 = processedAddress1 + townOverflow;
    }
    
    // Step 3: If address1 exceeds 10 characters, move overflow to address2
    if (processedAddress1.length > 10) {
      const address1Overflow = processedAddress1.substring(10);
      processedAddress1 = processedAddress1.substring(0, 10);
      processedAddress2 = processedAddress2 + address1Overflow;
    }
    
    // Step 4: Trim address2 to max 30 characters
    if (processedAddress2.length > 30) {
      processedAddress2 = processedAddress2.substring(0, 30);
    }
    
    return { city: processedCity, town: processedTown, address1: processedAddress1, address2: processedAddress2 };
  }

  /**
   * 会員編集フォーム送信前に対象住所の address_custom_data のみ更新する。
   * 更新対象は data-customer-address-id があればその ID、無ければ data-default-address-id。
   * @returns {boolean} 更新に成功した場合 true、それ以外は false
   */
  controlCustomFields() {
    const currentCustomDataStr = document.getElementById('currentCustomData')?.textContent || "null";
    const currentCustomData = JSON.parse(currentCustomDataStr);

    if (!currentCustomData || !Array.isArray(currentCustomData.addresses)) {
      this.form.querySelector('input[name="mf.itfor.address_custom_data"]')?.remove();
      return false;
    }

    // 会員プロフィール用: customer_address_id があればそれ、無ければ default_address_id
    const targetAddressId = this.dataset.customerAddressId || this.dataset.defaultAddressId;
    if (!targetAddressId) return false;
    const addressCustomData = currentCustomData.addresses.find(item => item.id === +targetAddressId);

    if (!addressCustomData) {
      this.form.querySelector('input[name="mf.itfor.address_custom_data"]')?.remove();
      return false;
    }

    const formData = new FormData(this.form);
    // 町域・番地は rits_* に既にセット済み（checkValidity 内で同一送信前に設定）。address_custom_data の更新対象として同じ値を保存
    const townForData = formData.get('mf.itfor.rits_address_town') || formData.get('town') || formData.get('address[town]') || '';
    const addressLine1ForData = formData.get('mf.itfor.rits_address_line1') || formData.get('address1') || formData.get('address[address1]') || '';

    // 既存の敬称データを取得（未設定の場合は空文字）
    const currentHonorific = addressCustomData.data.honorific || '';

    addressCustomData.data = {
      ...addressCustomData.data,
      sex: formData.get('gender'),
      birthday: formData.get('birthday'),
      last_name_kana: formData.get('lastNameKana'),
      first_name_kana: formData.get('firstNameKana'),
      job_title: formData.get('jobTitle'),
      honorific: currentHonorific,
      sender: formData.get('sender') === '1',
      phone_1: formData.get('phone_1') || '',
      phone_2: formData.get('phone_2') || '',
      phone_3: formData.get('phone_3') || '',
      town: townForData,
      address_line1: addressLine1ForData,
    };

    if (addressCustomData?.data?.gender) {
      delete addressCustomData.data.gender;
    }

    const addressCustomDataInput = this.form.querySelector('input[name="mf.itfor.address_custom_data"]');
    if (!addressCustomDataInput) return false;
    addressCustomDataInput.value = JSON.stringify(currentCustomData);
    return true;
  }

  controlFormFieldsByValue(event) {
    const input = event.target;
    const value = input.value;
    const elementsToControl = this.querySelectorAll('[data-cf-member-ship-type]');

    elementsToControl.forEach((element) => {
      const types = element.getAttribute('data-cf-member-ship-type').split(',');
      const industry_value = this.industry_select.value;

      if (element.tagName === 'OPTION') {
        if (types.includes(value)) {
          element.style.display = 'block';
        } else {
          if (industry_value === element.value) {
            this.industry_select.selectedIndex = '';
          }
          element.style.display = 'none';
        }
      } else if (types.includes(value)) {
        element.style.display = 'block';
        element.removeAttribute('disabled');
        element.querySelectorAll('input, select').forEach((input) => {
          input.removeAttribute('disabled');
        });
      } else {
        element.style.display = 'none';
        element.setAttribute('disabled', 'disabled');
        element.querySelectorAll('input, select').forEach((input) => {
          input.setAttribute('disabled', 'disabled');
        });
      }
    });
  }
}

class BirthDayFields extends HTMLElement {
  constructor() {
    super();
    this.birthday = this.querySelector('input[data-cf-validation="birthday"]');
    this.birthday_year = this.querySelector('select[name="birth_y"]');
    this.birthday_month = this.querySelector('select[name="birth_m"]');
    this.birthday_day = this.querySelector('select[name="birth_d"]');

    this.birthday_year.addEventListener('change', this.validateBirthDay.bind(this));
    this.birthday_month.addEventListener('change', this.validateBirthDay.bind(this));
    this.birthday_day.addEventListener('change', this.validateBirthDay.bind(this));
  }

  validateBirthDay() {
    const year = this.birthday_year.value;
    const month = this.birthday_month.value;
    const day = this.birthday_day.value;
    this.birthday.value = `${year}-${month}-${day}`;
  }
}

/**
 * `PhoneFields` クラスは、3つの電話番号フィールドを連結してhidden fieldに設定します。
 *
 * 主な機能:
 * - 3つの電話番号フィールド（phone_1, phone_2, phone_3）の値を連結
 * - 連結した値をhidden fieldのphoneに設定
 *
 * メソッド:
 * - `validatePhone()`: 3つのフィールドの値を連結してphoneフィールドに設定します。
 */
class PhoneFields extends HTMLElement {
  constructor() {
    super();
    this.phone = this.querySelector('input[name="phone"], input[name="address[phone]"]');
    this.phone_1 = this.querySelector('input[name="phone_1"]');
    this.phone_2 = this.querySelector('input[name="phone_2"]');
    this.phone_3 = this.querySelector('input[name="phone_3"]');

    if (this.phone_1) {
      this.phone_1.addEventListener('input', this.validatePhone.bind(this));
    }
    if (this.phone_2) {
      this.phone_2.addEventListener('input', this.validatePhone.bind(this));
    }
    if (this.phone_3) {
      this.phone_3.addEventListener('input', this.validatePhone.bind(this));
    }

    // 初期値を設定（既存の電話番号から3つのフィールドに分割）
    this.initializePhoneFields();
  }

  initializePhoneFields() {
    if (this.phone && this.phone.value && !this.phone_1.value && !this.phone_2.value && !this.phone_3.value) {
      const rawValue = this.phone.value;

      // ハイフン区切りの場合はそのまま分割（ユーザーの入力を保持）
      const hyphenParts = rawValue.split('-');
      if (hyphenParts.length === 3 && hyphenParts.every(p => /^\d+$/.test(p))) {
        this.phone_1.value = hyphenParts[0];
        this.phone_2.value = hyphenParts[1];
        this.phone_3.value = hyphenParts[2];
        return;
      }

      const phoneValue = rawValue.replace(/[-\s]/g, '');

      // 日本の電話番号パターンに基づいて分割
      let match = null;

      // 携帯電話（11桁）: 090-1234-5678, 080-1234-5678, 070-1234-5678
      if (phoneValue.length === 11 && /^(070|080|090)/.test(phoneValue)) {
        match = phoneValue.match(/^(\d{3})(\d{4})(\d{4})$/);
      }
      // 固定電話（10桁）市外局番3桁: 03-1234-5678, 06-1234-5678
      else if (phoneValue.length === 10 && /^(0[1-9])/.test(phoneValue)) {
        // 市外局番2桁のパターン（東京03、大阪06など）
        if (/^(03|06)/.test(phoneValue)) {
          match = phoneValue.match(/^(\d{2})(\d{4})(\d{4})$/);
        } else {
          // 市外局番3桁のパターン
          match = phoneValue.match(/^(\d{3})(\d{3})(\d{4})$/);
        }
      }
      // 固定電話（10桁）市外局番4桁: 0120-123-456
      else if (phoneValue.length === 10 && /^(0120|0800)/.test(phoneValue)) {
        match = phoneValue.match(/^(\d{4})(\d{3})(\d{3})$/);
      }
      // その他のケース（デフォルト）
      else if (phoneValue.length >= 10) {
        // 最後の4桁を固定、残りを2分割
        const lastPart = phoneValue.slice(-4);
        const remaining = phoneValue.slice(0, -4);
        const midLength = Math.ceil(remaining.length / 2);
        const firstPart = remaining.slice(0, midLength);
        const midPart = remaining.slice(midLength);

        this.phone_1.value = firstPart;
        this.phone_2.value = midPart;
        this.phone_3.value = lastPart;
        return;
      }

      if (match) {
        this.phone_1.value = match[1];
        this.phone_2.value = match[2];
        this.phone_3.value = match[3];
      }
    }
  }

  validatePhone() {
    const phone_1 = this.phone_1 ? this.phone_1.value : '';
    const phone_2 = this.phone_2 ? this.phone_2.value : '';
    const phone_3 = this.phone_3 ? this.phone_3.value : '';

    // Validate required: all fields must have values
    const allFieldsFilled = phone_1 !== '' && phone_2 !== '' && phone_3 !== '';

    if (this.phone) {
      if (allFieldsFilled) {
        this.phone.value = `${phone_1}${phone_2}${phone_3}`;
      } else {
        this.phone.value = '';
      }
      
      // Trigger change event for validation
      const changeEvent = new Event('change', { bubbles: true });
      this.phone.dispatchEvent(changeEvent);
    }
  }

  // ハイフン付きの電話番号を取得するメソッド
  getFormattedPhone() {
    const phone_1 = this.phone_1 ? this.phone_1.value : '';
    const phone_2 = this.phone_2 ? this.phone_2.value : '';
    const phone_3 = this.phone_3 ? this.phone_3.value : '';
    const parts = [phone_1, phone_2, phone_3].filter(part => part !== '');
    return parts.join('-');
  }
}

class RadioFields extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="hidden"]');
    this.radio_fields = this.querySelectorAll('input[type="radio"]');
    this.radio_fields.forEach((input) => {
      input.addEventListener('change', this.validateRequired.bind(this));
    });
  }

  validateRequired(event) {
    const input = event.target;
    this.input.value = input.value;
    const changeEvent = new Event('change');
    this.input.dispatchEvent(changeEvent); // change イベントをディスパッチ
  }
}

class RitsIdField extends HTMLElement {
  constructor() {
    super();

    this.rits_id_input = this.querySelector('input[name="mf.itfor.rits_id"]');
    this.new_rits_id_input = this.querySelector('input[name="newRITSID"]');
    this.rits_id_update_button = this.querySelector('button[type="button"]');
    this.rits_new_regist_status = this.querySelector('input[name="mf.itfor.rits_new_regist_status"]');
    this.ritsIdField = this.closest('rits-id-field');
    this.wrapForm = this.closest('wrap-form-with-properties') || this.closest('customer-fields')?.closest('wrap-form-with-properties');

    this.rits_id_input.addEventListener('change', this.handleRitsIdInput.bind(this));
    this.new_rits_id_input && this.new_rits_id_input.addEventListener('change', this.handleNewRitsIdInput.bind(this));
  }

  handleRitsIdInput() {
    const ritsId = this.rits_id_input.value;

    if (!this.new_rits_id_input) return;

    if (ritsId) {
      this.new_rits_id_input.disabled = true;
      this.new_rits_id_input.checked = false;
    } else {
      this.new_rits_id_input.disabled = false;
      this.new_rits_id_input.checked = true;
    }

    const event = new Event('change');
    this.new_rits_id_input.dispatchEvent(event);
  }

  handleNewRitsIdInput() {
    const isCreateNewRitsId = this.new_rits_id_input.checked;
    localStorage.setItem('isCreateNewRitsId', isCreateNewRitsId);

    if (isCreateNewRitsId === true) {
      if (this.wrapForm?.getAttribute('data-is-rits-card-number-pending') === 'true') {
        this.rits_new_regist_status.value = 'None';
      } else {
        this.rits_new_regist_status.value = 'Requesting';
      }
    } else {
      this.rits_new_regist_status.value = 'None';
    }
  }

}

class AddressFields extends CustomerFields {
  constructor() {
    super();

    // type
    this.addressType = this.dataset.type;
    this.target = this.dataset.target;

    // Remove the submit event listener from the parent class
    this.removeSubmitButtonEvent();

    // Add a new submit event listener specific to this class
    // For edit address modal, submit button is outside address-fields - find it in parent modal
    this.submit_button = this.querySelector('button.address-form__actions--add')
      || this.closest('wl-edit-address-modal')?.querySelector('#wlModalButtonSubmit_EditShipping')
      || this.closest('wl-shipping-modal')?.querySelector('#wlModalButtonSubmit');
    this.addSubmitButtonEvent();

    //form
    this.addressId = this.dataset.addressId;
    this.form?.addEventListener('submit', this.submit.bind(this));
    
    // form custom events
    if (!this.querySelector('button.address-form__actions--add')) {
      this.form?.addEventListener('custom-submit', this.__checkValidityListener);
      // Re-enable submit button when user corrects form (e.g. fills required fields after validation failed)
      this._setupSubmitButtonReEnableListeners();
    }
    // アドレス用バリデーションを入力時に再実行（エラーを即時解除）
    this._setupAddressValidationListeners();
  }

  _setupAddressValidationListeners() {
    if (!this.form) return;
    const run = () => this._validateAddressForm();
    const names = ['address[zip]', 'address[first_name]', 'address[last_name]', 'address[city]', 'address[town]', 'address[address1]', 'address[address2]', 'phone_1', 'phone_2', 'phone_3'];
    names.forEach((name) => {
      const el = this.form.elements.namedItem(name) || this.form.querySelector(`input[name="${name}"]`);
      if (el) {
        el.addEventListener('input', run);
        el.addEventListener('change', run);
      }
    });
  }

  /**
   * Setup listeners to enable/disable submit button based on form validity
   * Button disabled when there are errors, enabled only when form is valid
   */
  _setupSubmitButtonReEnableListeners() {
    if (!this.form || !this.submit_button) return;

    const updateSubmitButtonState = () => {
      if (!this.submit_button) return;
      const hasError = this.querySelector('.cf-error[style*="display: block"]');
      const isValid = !hasError && this.form.checkValidity();
      this.submit_button.disabled = !isValid;
      if (isValid) {
        this.submit_button.removeAttribute('aria-busy');
      }
    };

    const formInputs = this.form.querySelectorAll('input, select');
    formInputs.forEach((input) => {
      input.addEventListener('input', updateSubmitButtonState);
      input.addEventListener('change', updateSubmitButtonState);
    });
  }

  /**
   * Update submit button state (enabled/disabled) - call when modal opens with data
   */
  updateSubmitButtonVisibility() {
    if (!this.submit_button || !this.closest('wl-edit-address-modal')) return;
    const hasError = this.querySelector('.cf-error[style*="display: block"]');
    const isValid = !hasError && this.form?.checkValidity();
    this.submit_button.disabled = !isValid;
  }

  /**
   * アドレスフォーム用の追加バリデーション（カートの入力チェックに合わせる）
   * 郵便番号7桁・電話10〜11桁・各項目の桁数
   */
  _validateAddressForm() {
    if (!this.form) return;
    const zipInput = this.form.elements.namedItem('address[zip]');
    const phoneHidden = this.form.querySelector('input[name="address[phone]"]');
    const phone1 = this.form.elements.namedItem('phone_1');
    const phone2 = this.form.elements.namedItem('phone_2');
    const phone3 = this.form.elements.namedItem('phone_3');

    const MSG_ZIP = '郵便番号は7桁の数字で入力してください（例：1538001）';
    const MSG_PHONE = '電話番号は合計で10〜11桁の数字で入力してください（例：090-1234-5678）';

    if (zipInput) {
      const zipVal = (zipInput.value || '').replace(/-/g, '');
      if (zipVal && !/^\d{7}$/.test(zipVal)) {
        this.showAlert(zipInput, MSG_ZIP);
      } else {
        this.hideAlert(zipInput, MSG_ZIP);
      }
    }

    if (phoneHidden && phone1 && phone2 && phone3) {
      const full = (phone1.value || '') + (phone2.value || '') + (phone3.value || '');
      if (full && !/^\d{10,11}$/.test(full)) {
        this.showAlert(phoneHidden, MSG_PHONE);
      } else {
        this.hideAlert(phoneHidden, MSG_PHONE);
      }
    }

    const maxLengthFields = [
      { name: 'address[first_name]', label: 'お名前', max: 16 },
      { name: 'address[last_name]', label: '名前', max: 16 },
      { name: 'address[city]', label: '市区町村', max: 12 },
      { name: 'address[town]', label: '町域', max: 16 },
      { name: 'address[address1]', label: '番地', max: 16 },
      { name: 'address[address2]', label: '建物名、部屋番号等', max: 30 },
    ];
    maxLengthFields.forEach(({ name, label, max }) => {
      const input = this.form.elements.namedItem(name);
      if (!input) return;
      const val = (input.value || '').trim();
      const msg = `${label}は${max}文字以内で入力してください`;
      if (val.length > max) {
        this.showAlert(input, msg);
      } else {
        this.hideAlert(input, msg);
      }
    });
  }

  checkValidity() {
    this._validateAddressForm();
    super.checkValidity();
    if (this.submit_button && (this.closest('wl-edit-address-modal') || this.closest('wl-shipping-modal'))) {
      const hasError = this.querySelector('.cf-error[style*="display: block"]');
      const isValid = !hasError && this.form?.checkValidity();
      if (!isValid) {
        // Validation failed - re-enable so user can fix and resubmit
        this.submit_button.disabled = false;
        this.submit_button.removeAttribute('aria-busy');
      }
      // When validation passes: keep button disabled (already disabled on click)
    }
  }

  removeSubmitButtonEvent() {
    this.submit_button?.removeEventListener('click', this.__checkValidityListener);
  }

  addSubmitButtonEvent() {
    this.submit_button?.addEventListener('click', this.__checkValidityListener);
  }

  async submit(event) {
    try {
      event.preventDefault();

      // Prevent double submission with a flag
      if (this._isSubmitting) {
        console.warn('AddressFields.submit: already submitting, ignoring duplicate call');
        return;
      }
      this._isSubmitting = true;

      // Disable submit button to prevent double submission
      if (this.submit_button) {
        this.submit_button.disabled = true;
        this.submit_button.setAttribute('aria-busy', 'true');
      }
      
      if (this.target === "ADD_ADDRESS_TO_SETTINGS") {// add in cart page multi shipping settings
        const multiShippingItem = this.closest('wl-multi-shipping')?.querySelector('wl-multi-shipping-item');

        if (!multiShippingItem) {
          console.log("Cannot find <wl-multi-shipping-item>!!");
          // Re-enable button on error
          if (this.submit_button) {
            this.submit_button.disabled = false;
            this.submit_button.removeAttribute('aria-busy');
          }
          return;
        }

        const formData = new FormData(this.form);
        const formDataObject = {};
        for (let [key, value] of formData.entries()) {
          formDataObject[key] = value;
        }

        multiShippingItem.dispatchEvent(
          new CustomEvent('add-address', {
            detail: {
              formData: formDataObject
            },
            bubbles: true,
            cancelable: true,
          }),
        );
        
        // Re-enable button after dispatch
        if (this.submit_button) {
          this.submit_button.disabled = false;
          this.submit_button.removeAttribute('aria-busy');
        }
      } else {
        if (this.addressId) {
          // edit existing address
          await this.edit();
        } else {
          // add new address
          await this.add();
        }
        
        // Re-enable button after completion (in case of error or redirect)
        if (this.submit_button) {
          this.submit_button.disabled = false;
          this.submit_button.removeAttribute('aria-busy');
        }
      }
    } catch (error) {
      console.error("Error submitting address form:", error);
      // Re-enable button on error
      if (this.submit_button) {
        this.submit_button.disabled = false;
        this.submit_button.removeAttribute('aria-busy');
      }
      return;
    } finally {
      this._isSubmitting = false;
    }
  }

  async add() {
    const formData = new FormData(this.form);

    // Get address values from form
    const town = formData.get('address[town]') || '';
    const address1 = formData.get('address[address1]') || '';
    
    // Handle address overflow logic: city -> town -> address1 -> address2
    const processedAddress = this.processAddressData && this.processAddressData(
      formData.get('address[city]') || '',
      town,
      address1,
      formData.get('address[address2]') || ''
    );
    
    // 町域と番地を結合して Shopify address1 に送る（要件: 番地と合わせて address1 に登録）
    let finalAddress1;
    if (processedAddress) {
      const townPart = processedAddress.town || '';
      const line1Part = processedAddress.address1 || '';
      finalAddress1 = [townPart, line1Part].filter(Boolean).join(' ').trim() || line1Part || townPart;
    } else {
      finalAddress1 = [town, address1].filter(Boolean).join(' ').trim() || address1 || town;
    }
    const townForCustomData = processedAddress ? (processedAddress.town || '') : town;
    const address1ForCustomData = processedAddress ? (processedAddress.address1 || '') : address1;

    formData.set('address[address1]', finalAddress1);

    // カスタムフィールドの値を保存（メタフィールド更新用）
    const savedCustomFields = {
      jobTitle: formData.get('jobTitle') || '',
      email: formData.get('email') || '',
      honorific: formData.get('honorific') || '',
      sender: formData.get('sender'),
      phone_1: formData.get('phone_1') || '',
      phone_2: formData.get('phone_2') || '',
      phone_3: formData.get('phone_3') || '',
      lastNameKana: formData.get('lastNameKana') || '',
      firstNameKana: formData.get('firstNameKana') || '',
    };
    console.log('[AddressFields.add] savedCustomFields:', JSON.stringify(savedCustomFields));
    console.log('[AddressFields.add] town:', town, 'address1:', address1, 'townForCustomData:', townForCustomData, 'address1ForCustomData:', address1ForCustomData);

    // Shopify に送信する前に、Shopify が認識しないカスタムフィールドを FormData から除去
    formData.delete('address[town]');
    formData.delete('honorific');
    formData.delete('jobTitle');
    formData.delete('email');
    formData.delete('sender');
    formData.delete('phone_1');
    formData.delete('phone_2');
    formData.delete('phone_3');
    formData.delete('lastNameKana');
    formData.delete('firstNameKana');

    // 作成前の全アドレスIDを取得（作成後との差分で新規IDを特定するため）
    const existingIds = await this._getAllAddressIds();
    console.log('[AddressFields.add] Existing address IDs before create:', existingIds);

    console.log('[AddressFields.add] Sending POST to /account/addresses with redirect:manual');
    const createAddressResponse = await fetch('/account/addresses', {
      method: 'POST',
      body: formData,
      redirect: 'manual',
    });
    console.log('[AddressFields.add] Response:', createAddressResponse.type, createAddressResponse.status);

    // Shopify returns 302 on success; with redirect:'manual' this becomes opaqueredirect (type='opaqueredirect', status=0)
    if (createAddressResponse.type !== 'opaqueredirect' && !createAddressResponse.ok) {
      console.error("Error creating address:", createAddressResponse.status, createAddressResponse.type);
      return;
    }

    // 作成後の全アドレスIDをリトライ付きで取得し、差分から新規IDを特定
    // Section API のキャッシュ／反映遅延で直後は新規IDが見えないことがあるため
    console.log('[AddressFields.add] Address created, finding new ID by diff (with retry)...');
    const newestAddressId = await this._detectNewAddressId(existingIds);

    if (!newestAddressId) {
      // 既存IDフォールバックは既存住所のカスタムデータを汚染するリスクがあるため使わない
      console.error('[AddressFields.add] 新規アドレスIDを検出できませんでした。データ汚染防止のためカスタムデータの保存を中止します。');
      alert('住所は登録されましたが、追加情報の保存に失敗しました。アドレス帳から該当の住所を編集して再度保存してください。');
      window.location.href = '/pages/shipping-list';
      return;
    }
    console.log('[AddressFields.add] Newest address ID:', newestAddressId);

    const customData = {
      job_title: savedCustomFields.jobTitle,
      email: savedCustomFields.email,
      honorific: savedCustomFields.honorific,
      sender: savedCustomFields.sender === '1' || false,
      phone_1: savedCustomFields.phone_1,
      phone_2: savedCustomFields.phone_2,
      phone_3: savedCustomFields.phone_3,
      last_name_kana: savedCustomFields.lastNameKana,
      first_name_kana: savedCustomFields.firstNameKana,
      town: townForCustomData,
      address_line1: address1ForCustomData,
    };

    // 二重の安全策: 検出した「新規」IDが実は既存ID（_detectNewAddressId のバグや
    // 何らかの不整合）だった場合、既存住所のカスタムデータを汚染しないよう中止する
    if (existingIds.includes(+newestAddressId)) {
      console.error('[AddressFields.add] 検出した新規IDが既存IDリストに含まれています。データ汚染防止のため中止します。', {
        newestAddressId,
        existingIds,
      });
      alert('住所は登録されましたが、追加情報の保存に失敗しました。アドレス帳から該当の住所を編集して再度保存してください。');
      window.location.href = '/pages/shipping-list';
      return;
    }

    // サーバ最新値を取得（取得不可時はスナップショットへフォールバック）
    customerCustomData = await fetchLatestAddressCustomData();

    // 既存 data を取得して、未指定キー（birthday/sex 等）が消えないようマージする
    const existingEntryForAdd = customerCustomData.addresses.find((address) => address.id === +newestAddressId);
    const existingDataForAdd = existingEntryForAdd?.data || {};

    // 同じIDの既存エントリを全て除去してから新規データを追加（重複防止）
    customerCustomData.addresses = customerCustomData.addresses.filter((address) => address.id !== +newestAddressId);
    console.log('[AddressFields.add] After dedup, pushing new data for ID:', newestAddressId);
    customerCustomData.addresses.push({
      id: +newestAddressId,
      data: { ...existingDataForAdd, ...customData }
    });

    // デフォルト住所の場合、顧客メタフィールドにデフォルトアドレスIDも保存
    const isDefaultAddress = formData.get('address[default]') === 'on' || formData.get('address[default]') === '1';
    const metafieldsToUpdate = [
      {
        namespace: "itfor",
        key: `address_custom_data`,
        value: JSON.stringify(customerCustomData)
      }
    ];

    if (isDefaultAddress) {
      metafieldsToUpdate.push({
        namespace: "itfor",
        key: "default_address_id",
        value: String(newestAddressId)
      });
    }

    console.log('[AddressFields.add] Updating metafields for address ID:', newestAddressId);
    const updateCustomDataResponse = await fetch('/apps/ba-customerfields/update-customer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        defaultAddress: {},
        metafields: metafieldsToUpdate
      })
    });

    if (!updateCustomDataResponse.ok) {
      const errorData = await updateCustomDataResponse.json();
      console.error("Error updating customer custom data:", errorData);
      return;
    }

    window.location.href = '/pages/shipping-list';
  }

  async edit() {
    const formData = new FormData(this.form);

    // Get address values from form
    const town = formData.get('address[town]') || '';
    const address1 = formData.get('address[address1]') || '';
    
    // Handle address overflow logic: city -> town -> address1 -> address2
    const processedAddress = this.processAddressData && this.processAddressData(
      formData.get('address[city]') || '',
      town,
      address1,
      formData.get('address[address2]') || ''
    );
    
    // 町域と番地を結合して Shopify address1 に送る（要件: 番地と合わせて address1 に登録）
    let finalAddress1;
    if (processedAddress) {
      const townPart = processedAddress.town || '';
      const line1Part = processedAddress.address1 || '';
      finalAddress1 = [townPart, line1Part].filter(Boolean).join(' ').trim() || line1Part || townPart;
    } else {
      finalAddress1 = [town, address1].filter(Boolean).join(' ').trim() || address1 || town;
    }
    const townForCustomData = processedAddress ? (processedAddress.town || '') : town;
    const address1ForCustomData = processedAddress ? (processedAddress.address1 || '') : address1;

    formData.set('address[address1]', finalAddress1);

    // カスタムフィールドの値を保存（メタフィールド更新用）
    const savedCustomFields = {
      jobTitle: formData.get('jobTitle') || '',
      email: formData.get('email') || '',
      honorific: formData.get('honorific') || '',
      sender: formData.get('sender'),
      phone_1: formData.get('phone_1') || '',
      phone_2: formData.get('phone_2') || '',
      phone_3: formData.get('phone_3') || '',
      lastNameKana: formData.get('lastNameKana') || '',
      firstNameKana: formData.get('firstNameKana') || '',
    };

    // Shopify に送信する前に、Shopify が認識しないカスタムフィールドを FormData から除去
    formData.delete('address[town]');
    formData.delete('honorific');
    formData.delete('jobTitle');
    formData.delete('email');
    formData.delete('sender');
    formData.delete('phone_1');
    formData.delete('phone_2');
    formData.delete('phone_3');
    formData.delete('lastNameKana');
    formData.delete('firstNameKana');

    const editAddressResponse = await fetch(`/account/addresses/${this.addressId}`, {
      method: 'POST',
      body: formData,
      redirect: 'manual',
    });

    // Shopify returns 302 on success; with redirect:'manual' this becomes opaqueredirect (type='opaqueredirect', status=0)
    if (editAddressResponse.type !== 'opaqueredirect' && !editAddressResponse.ok) {
      console.error("Error editing address:", editAddressResponse.status, editAddressResponse.type);
      return;
    }


    // サーバ最新値を取得（取得不可時はスナップショットへフォールバック）
    customerCustomData = await fetchLatestAddressCustomData();

    const editedAddress = customerCustomData?.addresses?.find((address) => address.id === +this.addressId);

    const customData = {
      job_title: savedCustomFields.jobTitle,
      email: savedCustomFields.email,
      honorific: savedCustomFields.honorific,
      sender: savedCustomFields.sender === '1' || false,
      phone_1: savedCustomFields.phone_1,
      phone_2: savedCustomFields.phone_2,
      phone_3: savedCustomFields.phone_3,
      last_name_kana: savedCustomFields.lastNameKana,
      first_name_kana: savedCustomFields.firstNameKana,
      town: townForCustomData,
      address_line1: address1ForCustomData,
    };

    // 既存 data を取得して、未指定キー（birthday/sex 等）が消えないようマージする
    const existingDataForEdit = editedAddress?.data || {};

    // 同じIDの既存エントリを全て除去してから新規データを追加（重複防止）
    customerCustomData.addresses = customerCustomData.addresses.filter((address) => address.id !== +this.addressId);
    customerCustomData.addresses.push({
      id: +this.addressId,
      data: { ...existingDataForEdit, ...customData }
    });

    // デフォルト住所の場合、顧客メタフィールドも更新
    const isDefaultAddress = formData.get('address[default]') === 'on' || formData.get('address[default]') === '1';
    const metafieldsToUpdate = [
      {
        namespace: "itfor",
        key: `address_custom_data`,
        value: JSON.stringify(customerCustomData)
      }
    ];

    if (isDefaultAddress) {
      // デフォルトアドレスIDを保存（Shopify の default_address の管理用）
      metafieldsToUpdate.push({
        namespace: "itfor",
        key: "default_address_id",
        value: String(this.addressId)
      });
      // rits_*（住所・電話の会員プロフィール用メタフィールド）は会員編集（/pages/account-edit）からのみ更新する。
      // アドレス帳でデフォルト住所を変更しても rits_* は送信しない（ITFOR_WL-781_2）。
    }

    const updateCustomDataResponse = await fetch('/apps/ba-customerfields/update-customer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        defaultAddress: {},
        metafields: metafieldsToUpdate
      })
    });

    if (!updateCustomDataResponse.ok) {
      const errorData = await updateCustomDataResponse.json();
      console.error("Error updating customer custom data:", errorData);
      return;
    }

    window.location.href = '/pages/shipping-list';
  }

  async _getAllAddressIds() {
    try {
      // 1ページ目を取得して総ページ数を確認（Section API のキャッシュを避けるため毎回バスター付与）
      const cacheBuster = `_=${Date.now()}`;
      const firstPageResponse = await fetch(
        `/account/addresses?section=wl-new-added-address&page=1&${cacheBuster}`,
        { cache: 'no-store' }
      );
      const firstPageText = await firstPageResponse.text();
      const firstPageHtml = new DOMParser().parseFromString(firstPageText, 'text/html');

      const totalPagesEl = firstPageHtml.querySelector('#totalPages');
      const totalPages = totalPagesEl ? parseInt(totalPagesEl.textContent, 10) : 1;

      const firstPageIds = this._parseAddressIds(firstPageHtml);
      console.log('[AddressFields._getAllAddressIds] Page 1 IDs:', firstPageIds, 'Total pages:', totalPages);

      if (totalPages <= 1) {
        return firstPageIds;
      }

      // 2ページ目以降を並列取得
      const remainingPages = [];
      for (let page = 2; page <= totalPages; page++) {
        remainingPages.push(
          fetch(
            `/account/addresses?section=wl-new-added-address&page=${page}&${cacheBuster}`,
            { cache: 'no-store' }
          )
            .then((res) => res.text())
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              return this._parseAddressIds(html);
            })
        );
      }

      const remainingResults = await Promise.all(remainingPages);
      const allIds = firstPageIds.concat(...remainingResults);
      console.log('[AddressFields._getAllAddressIds] All IDs across', totalPages, 'pages:', allIds);
      return allIds;
    } catch (e) {
      console.error('[AddressFields._getAllAddressIds] Error:', e);
      return [];
    }
  }

  /**
   * 新規住所作成直後、Section API のキャッシュ／反映遅延で新規IDが取得できないことがあるため、
   * 既存IDリストとの差分で新規IDを検出する。差分が出るまで指定回数リトライする。
   *
   * @param {number[]} existingIds 作成前の全アドレスID
   * @param {number} maxRetries 最大リトライ回数
   * @param {number} baseDelay 初回待機ミリ秒（指数的に増加）
   * @returns {Promise<number | null>} 検出した新規ID。検出できなければ null
   */
  async _detectNewAddressId(existingIds, maxRetries = 5, baseDelay = 300) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        // 反映待ち（指数バックオフ）
        await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, attempt - 1)));
      }
      const updatedIds = await this._getAllAddressIds();
      const newIds = updatedIds.filter((id) => !existingIds.includes(id));
      console.log(`[AddressFields._detectNewAddressId] attempt=${attempt + 1} newIds=${JSON.stringify(newIds)}`);
      if (newIds.length > 0) {
        return newIds[0];
      }
    }
    return null;
  }

  _parseAddressIds(html) {
    const el = html.querySelector('#allAddressIds');
    if (el && el.textContent.trim()) {
      return el.textContent.split(',').map((id) => +id).filter(Boolean);
    }
    return [];
  }

  // NOTE: 旧 getNewestAddressId() は「住所一覧の最後の要素 = 新規住所」と決め打ちしていたが、
  //       Shopify の customer.addresses は登録の新しい順を保証しないため誤った住所を返し、
  //       依頼主のカナ・町域・番地が配送先へ書き込まれる原因になっていた。
  //       新規住所の特定は _detectNewAddressId()(作成前後の差分)を、
  //       会員本人の住所は customer_address_id / default_address.id を使うこと。

  _isAddressExistsInCustomData(addressId) {
    if (!customerCustomData || !customerCustomData.addresses) {
      return false;
    }
    return customerCustomData.addresses.some((address) => address.id === addressId);
  }

  setAddressId(addressId) {
    this.addressId = addressId;
    this.setAttribute('data-address-id', addressId);
  }
}

class ResetPasswordFields extends CustomerFields {
  constructor() {
    super();
    this.password_input = this.querySelector('input[name="customer[password]"]');
    this.password_confirm_input = this.querySelector('input[name="customer[password_confirmation]"]');
    
    if (this.password_input) {
      this.password_input.addEventListener('change', this.validatePassword.bind(this));
    }
    
    if (this.password_confirm_input) {
      this.password_confirm_input.addEventListener('change', this.validatePasswordMatch.bind(this));
    }
  }
  
  // Execute when form submit button is clicked
  checkValidity(event) {
    if (event) {
      event.preventDefault();
    }
    if (this.password_input) {
      this.validatePassword({ target: this.password_input });
    }
    
    if (this.password_confirm_input) {
      this.validatePasswordMatch({ target: this.password_confirm_input });
    }
    
    const hasError = this.querySelector('.cf-error[style*="display: block"]');
    
    if (!hasError && this.form && this.form.checkValidity()) {
      // Disable submit button to prevent double submission
      if (this.submit_button) {
        this.submit_button.disabled = true;
        this.submit_button.setAttribute('aria-busy', 'true');
      }
      this.form.submit();
    } else {
      // Re-enable submit button if there are validation errors
      if (this.submit_button) {
        this.submit_button.disabled = false;
        this.submit_button.removeAttribute('aria-busy');
      }
      
      // scroll to the first error
      const firstError = this.querySelector('dd:not([disabled]) .cf-error[style*="display: block"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }
}

customElements.define('customer-fields', CustomerFieldsSwitcher);
customElements.define('birthday-fields', BirthDayFields);
customElements.define('phone-fields', PhoneFields);
customElements.define('radio-fields', RadioFields);
customElements.define('rits-id-field', RitsIdField);
customElements.define('address-fields', AddressFields);
customElements.define('reset-password-fields', ResetPasswordFields);

// 初回のみ: customer_address_id が空で住所が1件以上ある場合にメタフィールドを1回保存する（会員トップ・会員編集で実行）
document.addEventListener('DOMContentLoaded', function () {
  const cfg = window.__wlNeedCustomerAddressIdSave;
  if (!cfg || !cfg.addressIdToSave) return;
  if (sessionStorage.getItem('wl_customer_address_id_sent')) return;
  fetch('/apps/ba-customerfields/update-customer', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      defaultAddress: {},
      metafields: [{ namespace: 'itfor', key: 'customer_address_id', value: String(cfg.addressIdToSave) }]
    })
  }).then(function (res) {
    if (res.ok) sessionStorage.setItem('wl_customer_address_id_sent', '1');
  }).catch(function (err) {
    console.warn('[wl-customer-fields] customer_address_id 初回保存の API 呼び出しに失敗しました:', err);
  });
});
